import Spacer from '@/common/components/spacer';
import SavingsCalculatorBlobContainer from './savings-calculator-blob-container';
import AccordionList from '@/common/components/accordion-list';
import CostBreakdown from '@/icons/cost-breakdown';
import { AccordionListItem } from '@/common/types';
import SubtractThick from '@/icons/subtract-thick';
import AddThick from '@/icons/add-thick';
import SavingsSourcesBodySection from './savings-sources-body-section';
import SavingsSourcesFooterSection from './savings-sources-footer-section';
import SavingsCalculatorCollapsibleSection from './savings-calculator-collapsible-section';

const sources: AccordionListItem[] = [
  {
    title: (
      <p className="body-strong font-sans">
        How are the vehicle sales prices from the savings calculator estimated?
      </p>
    ),
    content: [
      {
        body: (
          <SavingsSourcesBodySection body="The estimated vehicle wholesale and retail prices displayed in the savings calculator are sourced from Black Book, a reputable valuation website that specializes in providing car values to dealerships. Black Book utilizes a comprehensive data set that includes market trends, historical sales data, and various economic factors to generate accurate vehicle valuations. This ensures that the prices reflect current market conditions, helping users get a realistic understanding of vehicle worth." />
        ),
      },
    ],
    footer: (
      <SavingsSourcesFooterSection link="https://www.blackbook.com/vehicle-values/" />
    ),
    iconOverride: {
      openedIcon: <SubtractThick className="mr-[1px] mt-[10px] shrink-0 self-start" />,
      closedIcon: <AddThick className="mt-[4px] shrink-0 self-start" />,
    },
  },
  {
    title: (
      <p className="body-strong font-sans">
        What are the fees associated with buying a car?
      </p>
    ),
    content: [
      {
        body: (
          <SavingsSourcesBodySection body="When purchasing a car from a dealership, you'll encounter several types of fees that can significantly impact the overall cost. These fees are generally grouped into transaction fees, dealer fees, financing fees, and taxes. The Flexcar savings calculator takes these fees into account when calculating the total cost of buying a vehicle." />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Documentation Fee (Doc Fee)"
            body="Dealerships often charge a fee for processing and handling sales paperwork."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Title, Registration, and Inspection Fees"
            body="Every state requires vehicle titling and registration. Some dealerships handle this process for you, but you'll need to cover the associated costs. In addition, some states mandate a safety and emissions inspection before the car can be sold."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Sales Tax"
            body="Just like other goods, cars are subject to sales tax. The amount of tax depends on your state's sales tax rate, which can vary significantly."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Extended Warranty"
            body="Also known as a vehicle service contract, this optional coverage kicks in after the manufacturer's warranty expires. It covers specific repairs or replacements, depending on the contract."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Tire-and-Wheel Protection"
            body="This optional warranty covers the cost of repairing or replacing tires and wheels damaged by road hazards such as potholes, nails, or broken glass."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="GAP Insurance"
            body="Some lenders require Guaranteed Asset Protection (GAP) insurance. It covers the difference between your car's value and the amount you owe on your loan in case your car is totaled or stolen."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Loan Application Fee"
            body="Some dealerships or lenders may charge an upfront, non-refundable fee for processing your auto loan application."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            title="Vehicle Preparation Fee (Prep Fee)"
            body="This fee covers the dealership's cost to prepare the vehicle for delivery, which includes cleaning, removing protective coverings, and performing a final inspection."
          />
        ),
      },
    ],
    footer: (
      <SavingsSourcesFooterSection
        links={[
          'https://caredge.com/guides/understanding-car-dealer-fees',
          'https://caredge.com/guides/car-dealer-doc-fee-by-state',
          'https://www.lendingtree.com/auto/dealer-fees-when-buying-a-car/',
          'https://www.caranddriver.com/auto-loans/a41998078/dealer-fees-when-buying-car/',
          'https://www.bankrate.com/loans/auto-loans/dont-pay-unnecessary-dealer-fees/',
          'https://www.experian.com/blogs/ask-experian/what-is-loan-application-fee/',
        ]}
      />
    ),
    iconOverride: {
      openedIcon: <SubtractThick className="mr-[1px] mt-[10px] shrink-0 self-start" />,
      closedIcon: <AddThick className="mt-[4px] shrink-0 self-start" />,
    },
  },
  {
    title: (
      <p className="body-strong font-sans">How is the auto insurance cost estimated?</p>
    ),
    content: [
      {
        body: (
          <SavingsSourcesBodySection body="The cost of auto insurance varies based on several factors, including the vehicle you drive and personal details like your age, credit score, and driving history. The insurance cost estimate used in the Flexcar savings calculator is tailored to the specific vehicle you've selected and the personal information you provided." />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            body="The calculator uses data from insurance industry averages and key factors that impact premiums, including:"
            list={[
              'Vehicle Type: More expensive cars or cars with higher repair costs tend to have higher insurance premiums.',
              'Age and Driving Experience: Younger drivers or those with limited experience often face higher rates.',
              'Credit Score: In many states, a better credit score can result in lower premiums.',
            ]}
          />
        ),
      },
    ],
    footer: (
      <SavingsSourcesFooterSection
        links={[
          'https://www.bankrate.com/insurance/car/rates-by-credit-score/',
          'https://caredge.com/insurance',
        ]}
      />
    ),
    iconOverride: {
      openedIcon: <SubtractThick className="mr-[1px] mt-[10px] shrink-0 self-start" />,
      closedIcon: <AddThick className="mt-[4px] shrink-0 self-start" />,
    },
  },
  {
    title: (
      <p className="body-strong font-sans">
        How much can I expect to pay for vehicle maintenance?
      </p>
    ),
    content: [
      {
        body: (
          <SavingsSourcesBodySection body="The cost of vehicle maintenance depends on the type of car you drive, how often you drive it, and the specific services needed over time. On average, car owners can expect to pay around $900 per year for regular maintenance. However, this cost can vary based on the vehicle's age, make, and model." />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            body="The Flexcar savings calculator estimates your annual maintenance cost based on the number of miles you plan to drive and the vehicle that you selected."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            body="If your vehicle is no longer covered under warranty, you should budget for additional repair costs beyond routine maintenance, as older vehicles typically require more repairs."
          />
        ),
      },
      {
        body: (
          <SavingsSourcesBodySection
            className="mt-[8px]"
            body="Here's a breakdown of common vehicle maintenance costs:"
            list={[
              'Oil changes: $80 every 5,000-7,500 miles',
              'Tire rotations: $70 every 10,000 miles',
              'Engine and cabin air filter replacement: $100 every year',
              'Brake pad and rotors: $600 per axle, every 30,000-50,000 miles',
              'Four new tires: $800, every 50,000 miles',
              'Battery: $200, every 4 years',
            ]}
          />
        ),
      },
    ],
    footer: (
      <SavingsSourcesFooterSection
        links={[
          'https://caredge.com/maintenance',
          'https://www.aaa.com/autorepair/drivingcosts',
          'https://www.bankrate.com/loans/auto-loans/average-car-maintenance-costs',
          'https://living.acg.aaa.com/auto/how-much-to-budget-for-car-repairs',
          'https://www.consumeraffairs.com/automotive/average-car-maintenance-costs.html',
        ]}
      />
    ),
    iconOverride: {
      openedIcon: <SubtractThick className="mr-[1px] mt-[10px] shrink-0 self-start" />,
      closedIcon: <AddThick className="mt-[4px] shrink-0 self-start" />,
    },
  },
];

export default function SavingsSourcesSection() {
  return (
    <SavingsCalculatorBlobContainer className="bg-opacity-100 py-[16px]">
      <SavingsCalculatorCollapsibleSection
        title="Trusted data, clear methods"
        titleIcon={<CostBreakdown className="flex-shrink-0" />}
        subTrigger={
          <p className="body2 text-left font-sans">
            Discover the data and methods that demystify the true costs of buying and
            owning a car.
          </p>
        }
        keepSubTriggerVisible
      >
        <Spacer variant="height24" />
        <AccordionList
          accordionItems={sources}
          variant="default"
          separateItemsWithHorizontalRule
        />
      </SavingsCalculatorCollapsibleSection>
    </SavingsCalculatorBlobContainer>
  );
}
